import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser, setBusiness } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // Extract session_id from URL fragment
        const hash = location.hash || window.location.hash;
        const params = new URLSearchParams(hash.replace('#', ''));
        const sessionId = params.get('session_id');

        if (!sessionId) {
          console.error('No session_id found in URL');
          navigate('/login', { replace: true });
          return;
        }

        // Exchange session_id for user data
        const response = await axios.post(
          `${API}/auth/google/session`,
          { session_id: sessionId },
          { withCredentials: true }
        );

        if (response.data.success) {
          setUser(response.data.user);
          setBusiness(response.data.business);

          // Clear the URL fragment
          window.history.replaceState(null, '', window.location.pathname);

          // Redirect based on account type
          const accountType = response.data.user?.account_type;
          if (accountType === 'business' && response.data.business) {
            navigate('/business/dashboard', { replace: true, state: { user: response.data.user } });
          } else {
            navigate('/', { replace: true, state: { user: response.data.user } });
          }
        } else {
          throw new Error('Authentication failed');
        }
      } catch (error) {
        console.error('Auth callback error:', error);
        navigate('/login', { 
          replace: true, 
          state: { error: 'Google sign-in failed. Please try again.' } 
        });
      }
    };

    processAuth();
  }, [location.hash, navigate, setUser, setBusiness]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      <div className="text-center">
        <Loader2 className="h-12 w-12 animate-spin text-yellow-500 mx-auto mb-4" />
        <p className="text-white text-lg">Completing sign in...</p>
        <p className="text-gray-400 text-sm mt-2">Please wait while we verify your account</p>
      </div>
    </div>
  );
};

export default AuthCallback;
